import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import AzureAdUserSync from '../components/AzureAdUserSync';
import TablePagination from '../components/TablePagination';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const USERS_PAGE_SIZE = 25;

function sortList(list, key, dir) {
  if (!key) return list;
  return [...list].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'string'
      ? av.localeCompare(bv, undefined, { sensitivity: 'base' })
      : av - bv;
    return dir === 'ASC' ? cmp : -cmp;
  });
}

function SortTh({ label, sortKey, currentKey, currentDir, onSort, className = '' }) {
  const active = currentKey === sortKey;
  const Icon = active ? (currentDir === 'ASC' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? 'text-gray-700' : 'text-gray-400'}`} />
      </span>
    </th>
  );
}

export default function UsersList() {
  const { user: currentUser, currentCompanyId } = useAuth();
  const { hasPermission } = usePermissions();
  const [searchParams] = useSearchParams();
  const isPlatformOwner = currentUser?.isSuperAdmin;
  const [users, setUsers] = useState([]);
  const [platformOwners, setPlatformOwners] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showDemoteModal, setShowDemoteModal] = useState(false);
  const [demoteUser, setDemoteUser] = useState(null);
  const [demoteCompanyId, setDemoteCompanyId] = useState('');
  const [demoteRoleId, setDemoteRoleId] = useState('');
  const [moveUser, setMoveUser] = useState(null);
  const [moveTargetCompanyId, setMoveTargetCompanyId] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [activeTab, setActiveTab] = useState(
    isPlatformOwner ? (currentCompanyId ? 'all' : 'owners') : 'company'
  );
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', roleIds: [] });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    roleIds: []
  });
  const [saving, setSaving] = useState(false);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [promoteSearch, setPromoteSearch] = useState('');
  // Pagination & sort
  const [usersPage, setUsersPage] = useState(1);
  const [ownersPage, setOwnersPage] = useState(1);
  const [allUsersPage, setAllUsersPage] = useState(1);
  const [usersSortKey, setUsersSortKey] = useState('LastName');
  const [usersSortDir, setUsersSortDir] = useState('ASC');
  const [ownersSortKey, setOwnersSortKey] = useState('LastName');
  const [ownersSortDir, setOwnersSortDir] = useState('ASC');
  const [allUsersSortKey, setAllUsersSortKey] = useState('LastName');
  const [allUsersSortDir, setAllUsersSortDir] = useState('ASC');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [ownersSearch, setOwnersSearch] = useState('');
  const [allUsersSearch, setAllUsersSearch] = useState('');

  // Sync search param from URL (e.g. from global search navigation)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch) setSearchTerm(urlSearch);
  }, [searchParams]);

  useEffect(() => { setUsersPage(1); }, [searchTerm]);
  useEffect(() => { setOwnersPage(1); }, [ownersSearch]);
  useEffect(() => { setAllUsersPage(1); }, [allUsersSearch, selectedCompanyFilter]);

  useEffect(() => {
    if (isPlatformOwner) {
      loadPlatformOwners();
      loadAllUsers();
      loadCompanies();
    } else {
      loadUsers();
    }
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users');
      // Platform API returns array directly for platform owners
      setUsers(Array.isArray(response.data) ? response.data : (response.data.items || []));
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadPlatformOwners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/owners');
      const ownersData = response.data.platformOwners || response.data;
      setPlatformOwners(Array.isArray(ownersData) ? ownersData : []);
    } catch (err) {
      console.error('Failed to load platform owners:', err);
      setError(err.response?.data?.message || 'Failed to load platform owners');
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await api.get('/platform/users');
      const usersData = response.data.users || response.data;
      setAllUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      console.error('Failed to load all users:', err);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/platform/companies');
      const companiesData = response.data.companies || response.data;
      const normalized = Array.isArray(companiesData) ? companiesData : [];
      setCompanies(normalized);
      // Auto-filter to current company context so platform owners only see
      // the company they've switched into, not all companies at once
      if (currentCompanyId && normalized.length > 0) {
        const matched = normalized.find(c => c.CompanyID === currentCompanyId);
        if (matched) setSelectedCompanyFilter(matched.CompanyName);
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get('/roles', { params: currentCompanyId ? { companyId: currentCompanyId } : {} });
      const rolesData = response.data?.roles || response.data || [];
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handlePromoteUser = async (userId) => {
    if (!confirm('Are you sure you want to promote this user to Platform Owner? They will be removed from their company and gain full platform access.')) {
      return;
    }
    try {
      setSaving(true);
      await api.post('/platform/owners/promote', { userId });
      setShowPromoteModal(false);
      setPromoteSearch('');
      loadPlatformOwners();
      loadAllUsers();
    } catch (err) {
      console.error('Failed to promote user:', err);
      alert(err.response?.data?.error || 'Failed to promote user');
    } finally {
      setSaving(false);
    }
  };

  const handleDemoteOwner = async () => {
    if (!demoteCompanyId) {
      alert('Please select a company to assign the user to');
      return;
    }
    try {
      setSaving(true);
      await api.post(`/platform/owners/${demoteUser.UserID}/demote`, {
        companyId: demoteCompanyId,
        roleId: demoteRoleId || undefined
      });
      setShowDemoteModal(false);
      setDemoteUser(null);
      setDemoteCompanyId('');
      setDemoteRoleId('');
      loadPlatformOwners();
      loadAllUsers();
    } catch (err) {
      console.error('Failed to demote user:', err);
      alert(err.response?.data?.error || 'Failed to demote user');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePlatformOwnerStatus = async (userId, currentStatus) => {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this platform owner?`)) {
      return;
    }
    try {
      await api.patch(`/platform/owners/${userId}/status`, { isActive: !currentStatus });
      loadPlatformOwners();
    } catch (err) {
      console.error('Failed to toggle status:', err);
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleEditPlatformOwner = async (userId) => {
    try {
      setSaving(true);
      await api.put(`/platform/owners/${userId}`, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email
      });
      setEditingUserId(null);
      loadPlatformOwners();
    } catch (err) {
      console.error('Failed to update platform owner:', err);
      alert(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUserId(user.UserID);
    setEditForm({
      firstName: user.FirstName || '',
      lastName: user.LastName || '',
      email: user.Email || '',
      roleIds: user.RoleIDs ? user.RoleIDs.split(',').filter(Boolean) : [],
    });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditForm({ firstName: '', lastName: '', email: '', roleIds: [] });
  };

  const handleEditRoleToggle = (roleId) => {
    setEditForm(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const handleSaveEdit = async (userId) => {
    try {
      setSaving(true);
      const companyId = currentCompanyId;

      // Update user details
      await api.put(`/users/${userId}`, {
        email: editForm.email,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        roleIds: editForm.roleIds,
      });

      // Sync roles via dedicated endpoint for proper audit trail
      if (companyId) {
        await api.post(`/users/${userId}/roles`, {
          roleIds: editForm.roleIds,
          companyId,
        });
      }

      setEditingUserId(null);
      loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      alert(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleToggle = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId]
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.post('/users', formData);
      setShowCreateModal(false);
      setFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        roleIds: []
      });
      loadUsers();
    } catch (err) {
      console.error('Failed to create user:', err);
      alert(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveUserCompany = async () => {
    if (!moveUser || !moveTargetCompanyId) return;
    if (!confirm(`Move ${moveUser.Email} to ${companies.find(c => c.CompanyID === moveTargetCompanyId)?.CompanyName}? Their existing company roles will be removed.`)) return;
    try {
      setSaving(true);
      await api.put(`/platform/users/${moveUser.UserID}/company`, { companyId: moveTargetCompanyId });
      setShowMoveModal(false);
      setMoveUser(null);
      setMoveTargetCompanyId('');
      loadAllUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to move user');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivateUser = async (user) => {
    if (!confirm(`Re-activate ${user.Email} in ${user.CompanyName}? This will restore their membership and roles.`)) return;
    try {
      await api.put(`/platform/users/${user.UserID}/reactivate`);
      loadAllUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reactivate user');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'disable' : 'enable'} this user?`)) {
      return;
    }

    try {
      await api.patch(`/users/${userId}/status`, {
        isActive: !currentStatus
      });
      loadUsers();
    } catch (err) {
      console.error('Failed to update user status:', err);
      alert(err.response?.data?.message || 'Failed to update user status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  // Filtered users for "All Users" tab (platform owner context)
  // 'No Active Membership' = no company row OR membership is inactive (UCActive=0)
  const filteredAllUsers = allUsers
    .filter(u => {
      if (selectedCompanyFilter === '__none__') return !u.CompanyName || u.MembershipActive === false || u.MembershipActive === 0;
      if (selectedCompanyFilter) return u.CompanyName === selectedCompanyFilter;
      return true;
    })
    .filter(u => {
      if (!allUsersSearch) return true;
      const t = allUsersSearch.toLowerCase();
      return (
        (u.FirstName  && u.FirstName.toLowerCase().includes(t)) ||
        (u.LastName   && u.LastName.toLowerCase().includes(t)) ||
        (u.Email      && u.Email.toLowerCase().includes(t)) ||
        (u.Username   && u.Username.toLowerCase().includes(t)) ||
        (u.CompanyName && u.CompanyName.toLowerCase().includes(t))
      );
    });

  const filteredOwners = platformOwners.filter(o => {
    if (!ownersSearch) return true;
    const t = ownersSearch.toLowerCase();
    return (
      (o.FirstName && o.FirstName.toLowerCase().includes(t)) ||
      (o.LastName  && o.LastName.toLowerCase().includes(t)) ||
      (o.Email     && o.Email.toLowerCase().includes(t)) ||
      (o.Username  && o.Username.toLowerCase().includes(t))
    );
  });

  // Filter company users by search term
  const filteredUsers = searchTerm
    ? users.filter(u => {
        const term = searchTerm.toLowerCase();
        return (
          (u.FirstName && u.FirstName.toLowerCase().includes(term)) ||
          (u.LastName && u.LastName.toLowerCase().includes(term)) ||
          (u.Email && u.Email.toLowerCase().includes(term)) ||
          (u.Username && u.Username.toLowerCase().includes(term))
        );
      })
    : users;

  const sortedOwners = sortList(filteredOwners, ownersSortKey, ownersSortDir);
  const pagedOwners = sortedOwners.slice((ownersPage - 1) * USERS_PAGE_SIZE, ownersPage * USERS_PAGE_SIZE);

  const sortedAllUsers = sortList(filteredAllUsers, allUsersSortKey, allUsersSortDir);
  const pagedAllUsers = sortedAllUsers.slice((allUsersPage - 1) * USERS_PAGE_SIZE, allUsersPage * USERS_PAGE_SIZE);

  const sortedUsers = sortList(filteredUsers, usersSortKey, usersSortDir);
  const pagedUsers = sortedUsers.slice((usersPage - 1) * USERS_PAGE_SIZE, usersPage * USERS_PAGE_SIZE);

  function toggleSort(key, currentKey, currentDir, setKey, setDir) {
    if (currentKey === key) {
      setDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setKey(key);
      setDir('ASC');
    }
  }

  // Filtered promote candidates (non-platform-owner users)
  const promoteCandidates = promoteSearch.length >= 2
    ? allUsers.filter(u => 
        u.CompanyName && // has a company (not already platform owner)
        (u.Email?.toLowerCase().includes(promoteSearch.toLowerCase()) ||
         u.FirstName?.toLowerCase().includes(promoteSearch.toLowerCase()) ||
         u.LastName?.toLowerCase().includes(promoteSearch.toLowerCase()))
      )
    : [];

  // ─── Platform Owner View ───────────────────────────────────
  if (isPlatformOwner) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPromoteModal(true)}
              className="btn btn-warning"
            >
              <span>⬆</span>
              Promote to Platform Owner
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('owners')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'owners'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔑 Platform Owners ({platformOwners.length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            👥 All Users ({allUsers.length})
          </button>
        </div>

        {/* Platform Owners Tab */}
        {activeTab === 'owners' && (
          <div>
            {/* Search */}
            <div className="mb-4 relative">
              <input
                type="text"
                value={ownersSearch}
                onChange={(e) => setOwnersSearch(e.target.value)}
                placeholder="Search by name, email or username…"
                className="w-full md:w-80 pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {ownersSearch && (
                <button onClick={() => setOwnersSearch('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">✕</button>
              )}
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              {filteredOwners.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {ownersSearch ? `No platform owners matching "${ownersSearch}"` : 'No platform owners found'}
                </div>
              ) : (
                <>
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <colgroup>
                    <col className="w-[22%]" />
                    <col className="w-[25%]" />
                    <col className="w-[14%]" />
                    <col className="w-[10%]" />
                    <col className="w-[11%]" />
                    <col className="w-[9%]" />
                    <col className="w-[9%]" />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <SortTh label="User" sortKey="LastName" currentKey={ownersSortKey} currentDir={ownersSortDir} onSort={(k) => toggleSort(k, ownersSortKey, ownersSortDir, setOwnersSortKey, setOwnersSortDir)} />
                      <SortTh label="Email" sortKey="Email" currentKey={ownersSortKey} currentDir={ownersSortDir} onSort={(k) => toggleSort(k, ownersSortKey, ownersSortDir, setOwnersSortKey, setOwnersSortDir)} />
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auth</th>
                      <SortTh label="Last Login" sortKey="LastLoginAt" currentKey={ownersSortKey} currentDir={ownersSortDir} onSort={(k) => toggleSort(k, ownersSortKey, ownersSortDir, setOwnersSortKey, setOwnersSortDir)} />
                      <SortTh label="Status" sortKey="IsActive" currentKey={ownersSortKey} currentDir={ownersSortDir} onSort={(k) => toggleSort(k, ownersSortKey, ownersSortDir, setOwnersSortKey, setOwnersSortDir)} />
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pagedOwners.map((owner) => (
                      <tr key={owner.UserID} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {editingUserId === owner.UserID ? (
                            <div className="space-y-1">
                              <input type="text" value={editForm.firstName}
                                onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded" placeholder="First" />
                              <input type="text" value={editForm.lastName}
                                onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded" placeholder="Last" />
                            </div>
                          ) : (
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate">{owner.FirstName} {owner.LastName}</div>
                              <div className="text-xs text-gray-500 truncate">@{owner.Username}</div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {editingUserId === owner.UserID ? (
                            <input type="email" value={editForm.email}
                              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                          ) : (
                            <div className="text-sm text-gray-900 truncate">{owner.Email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 whitespace-nowrap">
                            🔑 Platform Owner
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                            owner.AuthType === 'sso' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {owner.AuthType === 'sso' ? 'SSO' : 'Local'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {owner.LastLoginAt ? new Date(owner.LastLoginAt).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap ${
                            owner.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {owner.IsActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {editingUserId === owner.UserID ? (
                            <div className="flex gap-2">
                              <button onClick={() => handleEditPlatformOwner(owner.UserID)}
                                disabled={saving} className="text-green-600 hover:text-green-900 font-medium text-xs">
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-900 font-medium text-xs">Cancel</button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <button onClick={() => handleEditUser(owner)}
                                className="text-blue-600 hover:text-blue-900 font-medium text-xs text-left">Edit</button>
                              {owner.UserID !== currentUser?.userId && (
                                <>
                                  <button onClick={() => handleTogglePlatformOwnerStatus(owner.UserID, owner.IsActive)}
                                    className={`${owner.IsActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} font-medium text-xs text-left`}>
                                    {owner.IsActive ? 'Disable' : 'Enable'}
                                  </button>
                                  <button onClick={() => { setDemoteUser(owner); setShowDemoteModal(true); }}
                                    className="text-orange-600 hover:text-orange-900 font-medium text-xs text-left">Demote</button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination page={ownersPage} pageSize={USERS_PAGE_SIZE} total={sortedOwners.length} onPageChange={setOwnersPage} />
                </>
              )}
            </div>
          </div>
        )}

        {/* All Company Users Tab */}
        {activeTab === 'all' && (
          <div>
            {/* Search + Company filter row */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <input
                  type="text"
                  value={allUsersSearch}
                  onChange={(e) => setAllUsersSearch(e.target.value)}
                  placeholder="Search name, email, company…"
                  className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {allUsersSearch && (
                  <button onClick={() => setAllUsersSearch('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">✕</button>
                )}
              </div>
              {companies.length > 0 && (
                <select
                  value={selectedCompanyFilter}
                  onChange={(e) => setSelectedCompanyFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All companies</option>
                  {companies.map((c) => (
                    <option key={c.CompanyID} value={c.CompanyName}>{c.CompanyName}</option>
                  ))}
                  <option value="__none__">— No Active Membership —</option>
                </select>
              )}
              <span className="text-sm text-gray-500 whitespace-nowrap">{filteredAllUsers.length} users</span>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              {filteredAllUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {allUsersSearch || selectedCompanyFilter ? 'No users match the current filters' : 'No users found'}
                </div>
              ) : (
                <>
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <colgroup>
                    <col className="w-[18%]" />
                    <col className="w-[20%]" />
                    <col className="w-[14%]" />
                    <col className="w-[10%]" />
                    <col className="w-[13%]" />
                    <col className="w-[9%]" />
                    <col className="w-[8%]" />
                    <col className="w-[8%]" />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <SortTh label="User" sortKey="LastName" currentKey={allUsersSortKey} currentDir={allUsersSortDir} onSort={(k) => toggleSort(k, allUsersSortKey, allUsersSortDir, setAllUsersSortKey, setAllUsersSortDir)} />
                      <SortTh label="Email" sortKey="Email" currentKey={allUsersSortKey} currentDir={allUsersSortDir} onSort={(k) => toggleSort(k, allUsersSortKey, allUsersSortDir, setAllUsersSortKey, setAllUsersSortDir)} />
                      <SortTh label="Company" sortKey="CompanyName" currentKey={allUsersSortKey} currentDir={allUsersSortDir} onSort={(k) => toggleSort(k, allUsersSortKey, allUsersSortDir, setAllUsersSortKey, setAllUsersSortDir)} />
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membership</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                      <SortTh label="Last Login" sortKey="LastLoginAt" currentKey={allUsersSortKey} currentDir={allUsersSortDir} onSort={(k) => toggleSort(k, allUsersSortKey, allUsersSortDir, setAllUsersSortKey, setAllUsersSortDir)} />
                      <SortTh label="Status" sortKey="IsActive" currentKey={allUsersSortKey} currentDir={allUsersSortDir} onSort={(k) => toggleSort(k, allUsersSortKey, allUsersSortDir, setAllUsersSortKey, setAllUsersSortDir)} />
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pagedAllUsers.map((user) => (
                      <tr key={user.UserID} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 truncate">{user.FirstName} {user.LastName}</div>
                          <div className="text-xs text-gray-500 truncate">@{user.Username}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 truncate">{user.Email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-500 truncate">
                            {user.CompanyName || <span className="italic text-gray-400">No Company</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {user.CompanyName ? (
                            <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap ${
                              user.MembershipActive ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.MembershipActive ? 'Active' : 'Inactive'}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-gray-100 text-gray-500">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-500 truncate">
                            {user.CompanyName ? (user.Roles || 'No Role') : '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {user.LastLoginAt ? new Date(user.LastLoginAt).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            if (!user.IsActive) return <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-red-100 text-red-800">Disabled</span>;
                            if (user.Status === 'PendingAccess') return <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
                            if (user.CompanyName && (user.MembershipActive === false || user.MembershipActive === 0)) return <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-orange-100 text-orange-700">Removed</span>;
                            return <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            {(user.MembershipActive === false || user.MembershipActive === 0) && user.CompanyName && (
                              <button
                                onClick={() => handleReactivateUser(user)}
                                className="text-green-600 hover:text-green-900 font-medium text-xs text-left"
                                title="Re-activate membership and roles"
                              >
                                Re-activate
                              </button>
                            )}
                            <button
                              onClick={() => { setMoveUser(user); setMoveTargetCompanyId(''); setShowMoveModal(true); }}
                              className="text-orange-600 hover:text-orange-900 font-medium text-xs text-left"
                              title="Move to different company"
                            >
                              Move
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination page={allUsersPage} pageSize={USERS_PAGE_SIZE} total={sortedAllUsers.length} onPageChange={setAllUsersPage} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Move User Company Modal */}
        {showMoveModal && moveUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Move User to Different Company</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {moveUser.Email} is currently in <strong>{moveUser.CompanyName || 'No Company'}</strong>.
                  Moving will remove all their existing company roles.
                </p>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Company</label>
                <select
                  value={moveTargetCompanyId}
                  onChange={(e) => setMoveTargetCompanyId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a company...</option>
                  {companies.filter(c => c.CompanyName !== moveUser.CompanyName).map(c => (
                    <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
                  ))}
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button onClick={() => { setShowMoveModal(false); setMoveUser(null); }} className="btn btn-secondary">Cancel</button>
                <button onClick={handleMoveUserCompany} disabled={saving || !moveTargetCompanyId} className="btn btn-warning">
                  {saving ? 'Moving...' : 'Move User'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Promote User Modal */}
        {showPromoteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Promote User to Platform Owner</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Search for a company user to promote. They will be removed from their company and gain full platform access.
                </p>
              </div>
              <div className="p-6">
                <input
                  type="text"
                  placeholder="Search by name or email (min 2 chars)..."
                  value={promoteSearch}
                  onChange={(e) => setPromoteSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
                />
                {promoteCandidates.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {promoteCandidates.slice(0, 20).map((user) => (
                      <div key={user.UserID} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div>
                          <div className="text-sm font-medium">{user.FirstName} {user.LastName}</div>
                          <div className="text-xs text-gray-500">{user.Email} • {user.CompanyName}</div>
                        </div>
                        <button
                          onClick={() => handlePromoteUser(user.UserID)}
                          disabled={saving}
                          className="btn btn-warning btn-sm"
                        >
                          {saving ? '...' : 'Promote'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : promoteSearch.length >= 2 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No matching users found</p>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">Type at least 2 characters to search</p>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button onClick={() => { setShowPromoteModal(false); setPromoteSearch(''); }} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Demote Platform Owner Modal */}
        {showDemoteModal && demoteUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Demote Platform Owner</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Demoting <strong>{demoteUser.FirstName} {demoteUser.LastName}</strong> ({demoteUser.Email}) from platform owner.
                  You must assign them to a company.
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Company *</label>
                  <select
                    value={demoteCompanyId}
                    onChange={(e) => setDemoteCompanyId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Company...</option>
                    {companies.map((c) => (
                      <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign Role (optional)</label>
                  <select
                    value={demoteRoleId}
                    onChange={(e) => setDemoteRoleId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Role</option>
                    {roles.map((role) => (
                      <option key={role.RoleID} value={role.RoleID}>{role.RoleName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button onClick={() => { setShowDemoteModal(false); setDemoteUser(null); }} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleDemoteOwner} disabled={saving || !demoteCompanyId} className="btn btn-danger">
                  {saving ? 'Demoting...' : 'Demote'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Company User View (original) ─────────────────────────
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSyncModal(true)}
            className="btn btn-success"
            title="Sync users from Azure AD"
          >
            <span>⬇</span>
            Sync from Azure AD
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <span>+</span>
            New User
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4 relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or username..."
          className="w-full md:w-96 pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
            ✕
          </button>
        )}
      </div>

      {filteredUsers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">{searchTerm ? `No users matching "${searchTerm}"` : 'No users found'}</p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-blue-600 hover:underline"
            >
              Create your first user
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="User" sortKey="LastName" currentKey={usersSortKey} currentDir={usersSortDir} onSort={(k) => toggleSort(k, usersSortKey, usersSortDir, setUsersSortKey, setUsersSortDir)} className="px-6" />
                <SortTh label="Email" sortKey="Email" currentKey={usersSortKey} currentDir={usersSortDir} onSort={(k) => toggleSort(k, usersSortKey, usersSortDir, setUsersSortKey, setUsersSortDir)} className="px-6" />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                <SortTh label="Last Login" sortKey="LastLogin" currentKey={usersSortKey} currentDir={usersSortDir} onSort={(k) => toggleSort(k, usersSortKey, usersSortDir, setUsersSortKey, setUsersSortDir)} className="px-6" />
                <SortTh label="Status" sortKey="IsActive" currentKey={usersSortKey} currentDir={usersSortDir} onSort={(k) => toggleSort(k, usersSortKey, usersSortDir, setUsersSortKey, setUsersSortDir)} className="px-6" />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pagedUsers.map((user) => (
                <tr key={user.UserID} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUserId === user.UserID ? (
                      <div className="space-y-1">
                        <input
                          type="text"
                          value={editForm.firstName}
                          onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="First"
                        />
                        <input
                          type="text"
                          value={editForm.lastName}
                          onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Last"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.FirstName} {user.LastName}
                          </div>
                          <div className="text-sm text-gray-500">@{user.Username}</div>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUserId === user.UserID ? (
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{user.Email}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingUserId === user.UserID ? (
                      <div className="space-y-1">
                        {roles.map((role) => (
                          <label key={role.RoleID} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editForm.roleIds.includes(role.RoleID)}
                              onChange={() => handleEditRoleToggle(role.RoleID)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            {role.RoleName}
                          </label>
                        ))}
                        {roles.length === 0 && <span className="text-xs text-gray-400">No roles defined</span>}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.RoleIDs
                          ? user.RoleIDs.split(',').filter(Boolean).map((rid) => {
                              const r = roles.find(x => x.RoleID === rid);
                              return r ? (
                                <span key={rid} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {r.RoleName}
                                </span>
                              ) : null;
                            })
                          : <span className="text-xs text-gray-400">No role</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.LastLogin ? new Date(user.LastLogin).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.IsActive ? 'Active' : 'Inactive'}
                    </span>
                    {user.IsSuperAdmin && (
                      <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                        Super Admin
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingUserId === user.UserID ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(user.UserID)}
                          disabled={saving}
                          className="text-green-600 hover:text-green-900 font-medium"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-gray-600 hover:text-gray-900 font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.UserID, user.IsActive)}
                          className={`${
                            user.IsActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                          } font-medium`}
                          disabled={user.UserID === currentUser?.userId}
                        >
                          {user.IsActive ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination page={usersPage} pageSize={USERS_PAGE_SIZE} total={sortedUsers.length} onPageChange={setUsersPage} />
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Create New User</h2>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">Minimum 6 characters</p>
              </div>

              {roles.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Roles
                  </label>
                  <div className="space-y-2">
                    {roles.map((role) => (
                      <label key={role.RoleID} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.roleIds.includes(role.RoleID)}
                          onChange={() => handleRoleToggle(role.RoleID)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {role.RoleName}
                          {role.Description && (
                            <span className="text-gray-500"> - {role.Description}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Azure AD Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Sync Users from Azure AD</h2>
              <button
                onClick={() => setShowSyncModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            <div className="p-6">
              <AzureAdUserSync />
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowSyncModal(false);
                  loadUsers();
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
